import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Mail, MapPin, Clock, Send } from "lucide-react";
import Seo from "../components/Seo";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || form.message.length < 5) {
      toast.error("Please fill out all fields.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/contact", form);
      if (typeof window !== "undefined") window.posthog?.capture?.("contact_message_submitted", { channel: "website" });
      toast.success("Message sent! We'll be in touch from beyondboardwoodwork@gmail.com.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="contact-workshop min-h-screen" data-testid="contact-page">
      <Seo
        title="Contact"
        description="Get in touch with Beyond Board Woodworking about custom laser-cut basswood signs, layered decor, and personalized pieces. We read every message."
        path="/contact"
      />
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <p className="text-xs uppercase tracking-[0.28em] text-[#D8A34B] mb-3">Contact</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#F5F0E6] leading-tight">Send us a note</h1>
          <p className="mt-6 text-[#B8B7B2] leading-relaxed">
            Project questions, sizing help, or just want to say hello — we read
            every message.
          </p>
          <ul className="mt-10 space-y-4">
            <li className="flex items-center gap-3 text-[#B8B7B2]">
              <Mail className="w-5 h-5 text-[#D8A34B]" />
              <a href="mailto:beyondboardwoodwork@gmail.com" className="hover:text-[#D8A34B]">
                beyondboardwoodwork@gmail.com
              </a>
            </li>
            <li className="flex items-center gap-3 text-[#B8B7B2]"><MapPin className="w-5 h-5 text-[#D8A34B]" /> By appointment only</li>
            <li className="flex items-center gap-3 text-[#B8B7B2]"><Clock className="w-5 h-5 text-[#D8A34B]" /> Replies within 1 business day</li>
          </ul>
        </div>

        <form
          onSubmit={submit}
          data-testid="contact-form"
          className="contact-natural-panel lg:col-span-7 rounded-3xl p-8 lg:p-10 space-y-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-stone-200">Name</Label>
              <Input
                id="name"
                data-testid="contact-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="bg-zinc-950/60 border-stone-100/10 text-stone-100 placeholder:text-stone-500 focus-visible:ring-[#D8A34B]"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-stone-200">Email</Label>
              <Input
                id="email"
                data-testid="contact-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="bg-zinc-950/60 border-stone-100/10 text-stone-100 placeholder:text-stone-500 focus-visible:ring-[#D8A34B]"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="msg" className="text-stone-200">Message</Label>
            <Textarea
              id="msg"
              data-testid="contact-message"
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              rows={7}
              className="bg-zinc-950/60 border-stone-100/10 text-stone-100 placeholder:text-stone-500 focus-visible:ring-[#D8A34B]"
            />
          </div>
          <Button data-testid="contact-submit" disabled={busy} className="contact-natural-button h-12 px-7">
            <Send className="w-4 h-4 mr-2" /> {busy ? "Sending…" : "Send message"}
          </Button>
        </form>
      </section>
    </div>
  );
}
